/**
 * CategoryIcon — renders a custom image icon for a category (the cropped 3D-style
 * purple glyphs the user provided). Falls back to the track's Lucide icon when
 * no image_icon is available (new tracks ship lucide-only until a 3D icon is baked).
 *
 * Props:
 *   image    - filename living in /public/icons/, e.g. "ebook-writing.jpg"
 *   lucide   - lucide icon name string, e.g. "shopping-bag" (used as fallback when image is null)
 *   size     - rendered pixel size (default 48)
 *   className - extra Tailwind classes
 *   glow     - whether to apply the brand purple drop-shadow (default true)
 */
import * as LucideIcons from "lucide-react";

function kebabToPascal(name) {
  return name
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("");
}

export function CategoryIcon({ image, lucide, size = 48, className = "", glow = true, loading }) {
  if (!image) {
    // Render the Lucide icon inside a soft purple frame as a graceful fallback.
    const iconName = lucide ? kebabToPascal(lucide) : "Sparkles";
    const Icon = LucideIcons[iconName] || LucideIcons.Sparkles;
    return (
      <div
        className={`bg-purple-500/10 border border-purple-500/40 inline-flex items-center justify-center ${className}`}
        style={{
          width: size,
          height: size,
          filter: glow ? "drop-shadow(0 0 14px rgba(168, 85, 247, 0.45))" : undefined,
        }}
      >
        <Icon
          size={Math.round(size * 0.5)}
          strokeWidth={1.6}
          className="text-purple-300"
        />
      </div>
    );
  }

  return (
    <img
      src={`/icons/${image}`}
      alt=""
      width={size}
      height={size}
      loading={loading}
      decoding="async"
      draggable={false}
      className={className}
      style={{
        display: "inline-block",
        objectFit: "contain",
        // Source images have a solid black background which blends with the app's
        // black UI via mix-blend-mode: screen — the icon glyph is the only thing visible.
        mixBlendMode: "screen",
        filter: glow ? "drop-shadow(0 0 14px rgba(168, 85, 247, 0.45))" : undefined,
      }}
    />
  );
}
